<template>
  <v-container class="default-layout fill-height pa-0">
    <v-row class="fill-height" justify="center">
      <v-col cols="12" class="pa-0">
        <router-view />
      </v-col>
    </v-row>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      location="top right"
      variant="flat"
      content-class="creative-snackbar"
    >
      <v-row class="align-center">
        <v-col v-if="showSnackbarLoader" cols="auto" class="pr-1">
          <v-progress-circular indeterminate size="small"></v-progress-circular>
        </v-col>
        <v-col>
          {{ snackbarMessage }}
        </v-col>
      </v-row>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, provide, ref } from 'vue'

import { setApiErrorNotifier } from '@/api/apiErrorHandler'
import { useAppStore } from '@/stores/app'
import { EmbedMessageType, notifyParent, mapColorToSubType, type EmbedMessagePayload } from '@/helpers/embedHelper'

const appStore = useAppStore()

const snackbar = ref<boolean>(false)
const snackbarMessage = ref()
const snackbarColor = ref()
const snackbarTimeout = ref(3000)
const showSnackbarLoader = ref(false)

const notify = (
  message: string,
  color: string,
  showLoader: boolean = false,
  persistSnackbar: boolean = false,
  embedMessageType: EmbedMessageType = EmbedMessageType.UserMessage,
) => {
  if (appStore.isInEmbedMode) {
    const payload: EmbedMessagePayload = {
      type: embedMessageType,
      subtype: mapColorToSubType(color),
      message,
    }
    try {
      notifyParent(payload)
      return
    } catch {
      // If notifying the parent fails, fall back to showing the snackbar.
    }
  }
  resetNotifyContent()

  snackbarMessage.value = message
  snackbarColor.value = color

  if (showLoader) {
    showSnackbarLoader.value = true
  }

  if (persistSnackbar) {
    snackbarTimeout.value = -1
  }

  snackbar.value = true
}

const updateNotifyContent = (message: string, color: string, timeout: number, showLoader: boolean) => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbarTimeout.value = timeout
  showSnackbarLoader.value = showLoader
}

const resetNotifyContent = () => {
  snackbarMessage.value = ''
  snackbarColor.value = ''
  showSnackbarLoader.value = false
  snackbarTimeout.value = 3000
}

onMounted(() => {
  setApiErrorNotifier(notify)
})

provide('notify', notify)
provide('updateNotifyContent', updateNotifyContent)
</script>

<style scoped lang="scss">
.default-layout {
  max-height: 100vh;
  max-width: 100vw;
  overflow: hidden;
  align-items: flex-start !important;

  > .v-row {
    margin: 0;
  }
}

::v-deep(.creative-snackbar) {
  margin-top: 12px;
}
</style>
